// The ticket 1149 output rule (decided 2026-10-19), the same on every surface and in
// hub/utils/effectiveAllergens.js and backend/api/helpers/effectiveAllergens.ts;
// components/AllergenDeclaration.vue renders it.
//   A. A bundle, or a line with any stored modifier row (quantity 0 included): only the maker sentence.
//   B. Allergens: the product's own tags, else "None" from the maker's declaration, else unknown.
//   C. Dietaries: the RAW claim flags as badges, else "None" from the declaration, else unknown.
// "Validate on input, present on output": hub's edit page locks a claim the allergen tags
// contradict and the backend switches one off on every save, and rows saved before that are found
// with GET /api/users/products/checkConflict. So the badges and the filter chips read flags raw.

type AllergenLike = { id?: number; name?: string } | null | undefined;
type ProductLike = Record<string, unknown> | null | undefined;

// Per-dish sentence ("this product"). No site surface renders it today — hub's per-dish label carries
// the same words in its content/*.json — so it is kept as the singular twin of the order-level
// sentence below and pinned against it in the utils spec, so the two cannot drift apart.
export const ALLERGEN_DISCLAIMER =
  "Allergen information is provided by the Maker and cannot be guaranteed. This product may contain traces of other allergens. For specific questions, please contact the maker directly.";

// Order-level sentence, for a note that sits under a LIST of dishes (the order summary, the
// print-menu footer). Under ten dishes "this product" reads as a caption for the last one.
export const ALLERGEN_DISCLAIMER_ORDER =
  "Allergen information is provided by the Maker and cannot be guaranteed. These products may contain traces of other allergens. For specific questions, please contact the maker directly.";

function asAllergens(v: unknown): AllergenLike[] {
  return Array.isArray(v) ? (v as AllergenLike[]) : [];
}
function asModifiers(v: unknown): Record<string, unknown>[] {
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

// Allergen NAMES (lowercase) off the product's own tags. Picked modifiers are never read: a line
// carrying any prints the maker sentence instead (case A).
export function effectiveAllergenNames(product: ProductLike): string[] {
  const names: string[] = [];
  for (const a of asAllergens((product || {}).allergens)) {
    const n = a?.name?.toLowerCase();
    if (n) names.push(n);
  }
  return names;
}

// Deduped, display-cased list ("Gluten", "Milk", ...).
export function effectiveAllergenDisplayList(product: ProductLike): string[] {
  const unique = [...new Set(effectiveAllergenNames(product))];
  return unique.map((n) => n.charAt(0).toUpperCase() + n.slice(1));
}

// Ticket 1149: "None" is only ever printed from the maker's stored declaration, never inferred
// from missing data; without one, the pre-1149 wording stays. Same strings as
// hub/utils/effectiveAllergens.js.
export const NO_KNOWN_ALLERGENS_TEXT = "None";
export const ALLERGENS_UNKNOWN_TEXT = "No allergen information available";

// An order line ({ product, modifiers }) or a merged product for a bundle.
export function isBundleLine(itemOrProduct: unknown): boolean {
  const o = (itemOrProduct || {}) as Record<string, unknown>;
  const product = o.product as Record<string, unknown> | undefined;
  return Boolean(o.isBundle || o.isBundleItem) || o.type === "Bundle" || product?.type === "Bundle";
}

// Never for a bundle, even one with no rows: its contents decide (docs/decisions.md 2026-10-08, D1).
export function declaresNoKnownAllergens(product: ProductLike): boolean {
  const p = (product || {}) as Record<string, unknown>;
  if (!p.no_known_allergens || showsMakerDisclaimer(p)) return false;
  return effectiveAllergenNames(p).length === 0;
}

// Case B of the output rule; a line that showsMakerDisclaimer never reaches it on screen.
export function effectiveAllergenText(product: ProductLike): string {
  const list = effectiveAllergenDisplayList(product);
  if (list.length) return list.join(", ");
  return declaresNoKnownAllergens(product) ? NO_KNOWN_ALLERGENS_TEXT : ALLERGENS_UNKNOWN_TEXT;
}

// The "Dietaries:" line when no badge renders (ticket 1149): "None" only from the maker's
// no_dietary_claims declaration. Same strings and rule as hub/utils/effectiveAllergens.js.
export const NO_DIETARY_CLAIMS_TEXT = "None";
export const NO_DIETARY_INFO_TEXT = "No dietary information available";
export const DIETARY_FLAGS = [
  "is_vegetarian",
  "is_vegan",
  "is_halal",
  "is_gluten_free",
  "is_nut_free",
  "is_dairy_free",
];

export function declaresNoDietaryClaims(product: ProductLike): boolean {
  const p = (product || {}) as Record<string, unknown>;
  if (!p.no_dietary_claims || showsMakerDisclaimer(p)) return false;
  return !DIETARY_FLAGS.some((flag) => Boolean(p[flag]));
}

// ---------------------------------------------------------------------------
// Store / menu dietary filters
// ---------------------------------------------------------------------------
// One filter chip from Store/AllergenFilter.vue. Either a dietary claim
// (`toggle`, e.g. is_nut_free) or a set of allergen NAMES to exclude (`list`,
// the "Allergens" chips built from /api/public/allergens). Temperature chips
// travel in the same array with neither key and must not filter anything.
export interface DietaryFilter {
  toggle?: string;
  list?: string[];
  is_active?: boolean;
}

// The product's own raw claim flag — what ProductAllergens.vue badges and the dietary toggle
// chips both read, so a filter and a badge can never disagree. A modifier can never grant one.
export function productClaimsDietary(product: ProductLike, flagKey: string): boolean {
  return Boolean((product || {})[flagKey]);
}

// Whether a product is free of every named allergen, matched against its allergen tags
// only — never against the product's name or description.
//
// A substring scan of free text is not an allergen check: "doughnuts" contains
// "nuts" and "Free From Gluten" contains "gluten", so the old text match hid a
// whole nut-free doughnut menu behind the "No Nuts" chip and hid gluten-free
// products behind "No Gluten".
//
// No tags is "unknown", not "free of everything" (ticket 1149): only the maker's declaration
// lets an untagged dish through. Fails closed when `allergens` was never loaded, too.
export function productFreeOfAllergens(product: ProductLike, names: string[]): boolean {
  if (!names.length) return true;
  if (!Array.isArray((product || {}).allergens)) return false;
  const effective = new Set(effectiveAllergenNames(product));
  if (!effective.size) return declaresNoKnownAllergens(product);
  return !names.some((n) => effective.has(String(n).toLowerCase()));
}

export function productPassesDietaryFilter(
  product: ProductLike,
  filter: DietaryFilter | null | undefined,
): boolean {
  if (!filter || filter.is_active === false) return true;
  // A bundle declares nothing of its own (case A), so no dietary or allergen chip can vouch for it.
  const bundle = isBundleLine(product);
  if (filter.list) return !bundle && productFreeOfAllergens(product, filter.list);
  if (filter.toggle) return !bundle && productClaimsDietary(product, filter.toggle);
  return true;
}

export function productPassesDietaryFilters(
  product: ProductLike,
  filters: DietaryFilter[] | null | undefined | false,
): boolean {
  if (!Array.isArray(filters) || !filters.length) return true;
  return filters.every((filter) => productPassesDietaryFilter(product, filter));
}

// ---------------------------------------------------------------------------
// Case A — the maker sentence. Port of hub/utils/effectiveAllergens.js; keep the two in step
// until the helper moves to shared/.
//
// A modifier carries its own allergen tags and dietary toggles, and nothing reconciles them with
// the base dish's, so a bundle or a line with stored option rows declares nothing and sends the reader
// to the maker instead. Introduced by ticket 1142 (2026-09-18) and made the permanent rule by
// ticket 1149 (decided 2026-10-19).
// ---------------------------------------------------------------------------

// Shown when a maker name could not be resolved, so the sentence still reads as an instruction
// and never degrades to "please contact  directly".
export const MODIFIED_DISH_FALLBACK_MAKER = "the maker";

// Takes the ORDER LINE (`{ product, modifiers }`) or a product carrying the customer's picks.
// Catalogue products carry `modifierGroups` (options on offer), never `modifiers`, so a store card
// only ever trips the bundle half.
//
// A stored modifier counts whatever its quantity: deselected options persist at `quantity: 0` on
// older rows, and when it is unclear whether a line was modified, declare less.
export function hasModifierRows(itemOrProduct: unknown): boolean {
  if (!itemOrProduct || typeof itemOrProduct !== "object") return false;
  return (
    asModifiers((itemOrProduct as Record<string, unknown>).modifiers).filter(Boolean).length > 0
  );
}

// Case A of the output rule: the line prints only makerDisclaimer, no badges, no allergen
// list, no "None". Same gate as hub and backend/api/helpers/effectiveAllergens.ts.
export function showsMakerDisclaimer(itemOrProduct: unknown): boolean {
  return isBundleLine(itemOrProduct) || hasModifierRows(itemOrProduct);
}

// The one copy of the sentence. Byte-identical to hub's so the label, the print menu and the
// order summary can never say three different things.
export function modifiedDishDisclaimer(makerName?: unknown): string {
  const maker = String(makerName ?? "").trim() || MODIFIED_DISH_FALLBACK_MAKER;
  return `This dish has been modified. For allergen and dietary information, please contact ${maker} directly or log in to your &Dine account to review the maker's page.`;
}

// Case A's sentence for a given line. A bundle was never modified, so it drops the opening clause;
// derived rather than retyped so ticket 1151's rewording of the rest reaches both (2026-10-20).
export function makerDisclaimer(line: unknown, makerName?: unknown): string {
  const sentence = modifiedDishDisclaimer(makerName);
  return isBundleLine(line) ? sentence.replace(/^This dish has been modified\. /, "") : sentence;
}
